import { t } from "i18next";

import { ChangeEvent, Fragment, useEffect, useRef, useState } from "react";

import { TextField, Autocomplete } from "@mui/material";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { useParams } from "react-router-dom";

import { AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { FilterDropdown } from "@/components/FilterDropdown.tsx/FilterDropdown";
import { InputField } from "@/components/InputField/InputField";
import { AssignStatus, InclusionFilterStatus, ListSourceType } from "@/enum/list.enum";
import { FitLevel } from "@/enum/fit-level.enum";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

import { Icon } from "@/components";

import { Information } from "@/components/Information/Information";

import { classNames } from "@/utils/classNames";

import { loadsOptionsWithoutSearch } from "./eagerOptionFilters";
import { formatRelativeDays } from "./relativeDateUtils";

import {
	IBooleanFilterType,
	IDateFilterType,
	IFilter,
	IFilterType,
	INumericFilterType,
	ITextFilterType,
} from "../constants/general-filter";
import { IFilterWithValues } from "../components/FilterModal";
import {
	ICurrentFilter,
	ICurrentFilterValue,
} from "../pages/ListEditFilter/interfaces/config-interface";
import { FilterCategory } from "../enums/filter.enum";

export const useFilters = ({
	filter,
	filterDefinitions,
}: {
	filter?: IFilterWithValues;
	sourceType?: ListSourceType;
	filterDefinitions?: IFilter[];
}) => {
	const { id = 0 } = useParams();

	const [filterType, setFilterType] = useState<{ [key: string]: IFilterType }>({});

	const [searchValue, setSearchValue] = useState<{ [key: string]: string }>();
	const [query, setQuery] = useState<{ [key: string]: string }>({});

	const searchTimer = useRef<NodeJS.Timeout | null>(null);

	const counter = useRef<number>(0);

	useEffect(() => {
		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
		}

		searchTimer.current = setTimeout(() => {
			searchValue &&
				Object.keys(searchValue).forEach((key) => {
					setQuery({ ...query, [key]: searchValue[key] });
				});
		}, 800);
	}, [searchValue]);

	const [filterValue, setFilterValue] = useState<IFilterWithValues>({ ...filter });

	useEffect(() => {
		if (filter) {
			const prepFilterType =
				filter &&
				Object.keys(filter).reduce((acc, curr) => {
					return { ...acc, [curr]: filter[curr].type };
				}, {});

			if (prepFilterType) {
				setFilterType(prepFilterType);
			}

			setFilterValue(filter);
		}
	}, [filter]);

	const [getFilterByValue] = useLazyGetFilterByPropertyQuery();

	const [inputValues, setInputValues] = useState<string>();

	const [isLoadingByPropertyName, setIsLoadingByPropertyName] = useState<string | null>(null);

	const [currentFilter, setCurrentFilter] = useState<ICurrentFilter>({});

	useEffect(() => {
		const getFilterValues = async () => {
			if (filterType) {
				const filtersToIterate = Object.keys(filterType);

				let currentFilterVaules: ICurrentFilter = { ...currentFilter };

				for await (const key of filtersToIterate) {
					if (
						key === "assignedStatus" &&
						(filterType[key] === "dropdown" || filterType[key] === "dropdownExclude")
					) {
						const statusValues = Object.values(AssignStatus)
							.filter((status) =>
								[
									AssignStatus.ASSIGNED,
									AssignStatus.RUNNING,
									AssignStatus.MESSAGE_SENT,
									AssignStatus.ENDED,
								].includes(status)
							)
							.map((status) => ({
								id: status,
								title: t(`inbox.assignStatus.${status}`),
							})) as ICurrentFilterValue[];

						currentFilterVaules = {
							...currentFilterVaules,
							[key]: statusValues,
						} as ICurrentFilter;
					} else if (
						key === "lastNodeType" &&
						(filterType[key] === "dropdown" || filterType[key] === "dropdownExclude")
					) {
						const nodeTypeKeys = [
							"emailNode",
							"linkedinNode",
							"linkedinInviteNode",
							"linkedinVisitNode",
							"linkedinInmailNode",
							"linkedinProfileVisit",
						];
						const nodeTypeValues = nodeTypeKeys.map((nodeKey) => ({
							id: nodeKey,
							title: t(`inbox.nodeType.${nodeKey}`),
						})) as ICurrentFilterValue[];

						currentFilterVaules = {
							...currentFilterVaules,
							[key]: nodeTypeValues,
						} as ICurrentFilter;
					} else if (
						key === "fitAssessment" &&
						(filterType[key] === "dropdown" || filterType[key] === "dropdownExclude")
					) {
						const fitLevelValues = Object.values(FitLevel).map((level) => ({
							id: level,
							title: t(`inbox.fitLevel.${level}`),
						})) as ICurrentFilterValue[];

						currentFilterVaules = {
							...currentFilterVaules,
							[key]: fitLevelValues,
						} as ICurrentFilter;
					} else if (
						filterDefinitions &&
						(filterType[key] === "dropdown" ||
							filterType[key] === "dropdownExclude" ||
							filterType[key] === "dropdownCustom" ||
							filterType[key] === "dropdownCustomExclude") &&
						filterDefinitions.find(
							(f) => f.name === key && f.filterByValue && f.filterByValue.length > 0
						)
					) {
						const filterDef = filterDefinitions.find((f) => f.name === key)!;
						const staticValues = filterDef.filterByValue.map(
							(item: { value?: string; id?: string; label?: string; title?: string }) => ({
								id: item.value ?? item.id ?? String(item),
								title: item.label ?? item.title ?? String(item),
							})
						) as ICurrentFilterValue[];

						currentFilterVaules = {
							...currentFilterVaules,
							[key]: staticValues,
						} as ICurrentFilter;
					} else if (
						(filterType[key] === "dropdown" ||
							filterType[key] === "dropdownExclude" ||
							filterType[key] === "dropdownCustom" ||
							filterType[key] === "dropdownCustomExclude") &&
						(query[key]?.length > 1 || loadsOptionsWithoutSearch(key))
					) {
						setIsLoadingByPropertyName(key);
						const filterValues = await getFilterByValue({
							propertyName: key,
							searchTerm: query?.[key] || "",
							listId: +id || 0,
						}).unwrap();

						if (filterValues?.data) {
							currentFilterVaules = {
								...currentFilterVaules,
								[key]: filterValues.data.map((rec) => {
									return {
										id: rec?.id?.toString() || rec.value,
										title: rec.value,
									} as ICurrentFilterValue;
								}) as ICurrentFilterValue[],
							} as ICurrentFilter;
						}

						setIsLoadingByPropertyName(null);
					}
				}

				setCurrentFilter({
					...currentFilterVaules,
				});
			}
		};

		getFilterValues();
	}, [filterType, query]);

	const [show, setShow] = useState<number[]>([]);
	const [prefill, setPrefill] = useState<boolean>(true);
	const [showGroup, setShowGroup] = useState<{ [key: string]: boolean }>({
		"Frequently used": true,
	});

	const getFilterLabel = (filter: IFilter) => t(filter.translationKey ?? `inbox.${filter.name}`);

	const renderFilters = (
		FilterDataType: IFilter[],
		hasCheckbox?: boolean,
		noTranslations?: boolean,
		showGroupedFilters?: boolean
	) => {
		const groupedFilters = FilterDataType.reduce((acc: { [key: string]: IFilter[] }, curr) => {
			if (curr.group) {
				acc[curr.group] = [...(acc[curr.group] || []), curr];
			} else {
				acc["Frequently used"] = [...(acc["Frequently used"] || []), curr];
			}

			return acc;
		}, {});

		if (hasCheckbox && prefill && FilterDataType?.length > 0) {
			FilterDataType.map((item, index) => {
				if (!show.includes(index) && Object.keys(filterType).includes(item.name)) {
					setShow([...show, index]);
				}
			});
		}

		return (
			<div className="flex flex-col w-full">
				{showGroupedFilters
					? [
							FilterCategory.FREQUENTLY_USED,
							FilterCategory.ADDITIONAL_ATTRIBUTES,
							FilterCategory.LEAD_CATEGORIZATION_AND_SCORING,
							...Object.keys(groupedFilters).filter(
								(group) =>
									group !== FilterCategory.FREQUENTLY_USED &&
									group !== FilterCategory.ADDITIONAL_ATTRIBUTES &&
									group !== FilterCategory.LEAD_CATEGORIZATION_AND_SCORING
							),
						].map((group, index) => {
							return (
								<div
									key={`${group}-${index}`}
									className={classNames(
										"border p-6 mb-8 w-full items-start rounded-md border-1 border-border-light",
										"border-inactive-item",
										"flex flex-col text-left gap-6",
										showGroup[group] ? "bg-background-light" : "bg-white"
									)}
								>
									<button
										className={classNames(
											"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
										)}
										onClick={() => {
											setShowGroup({ ...showGroup, [group]: !showGroup[group] });
										}}
									>
										<div className="grow">
											<div className="text-smd text-text font-bold">
												{group}{" "}
												{groupedFilters[group]?.find(
													(f) => filterType?.[f.name] && !filterType?.[f.name]?.includes("noFilter")
												) && (
													<span className="text-smd text-text font-bold">
														(
														{
															groupedFilters[group]?.filter(
																(f) =>
																	filterType?.[f.name] &&
																	!filterType?.[f.name]?.includes("noFilter")
															).length
														}
														)
													</span>
												)}
											</div>
										</div>
										<div
											className={classNames(
												"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
												showGroup[group]
													? "border-border-light bg-white"
													: "border-transparent bg-background-light"
											)}
										>
											<Icon
												className="text-text-meta group-hover:text-white w-[12px] h-auto"
												icon={showGroup[group] ? faChevronUp : faChevronDown}
											/>
										</div>
									</button>
									{showGroup[group] && (
										<div className="flex flex-col w-full">
											{groupedFilters[group]?.map((filter, index) => {
												return (
													<div
														key={`${filter.name}-${index}`}
														className="flex flex-row items-start w-full"
													>
														<div className="flex flex-row w-[200px] mr-8 items-center h-[48px]">
															{hasCheckbox ? (
																<Checkbox
																	isChecked={show.includes(index)}
																	onChange={() => {
																		setPrefill(false);
																		if (show.includes(index)) {
																			setShow(show.filter((s) => s !== index));

																			// remove from filterValue
																			const filteredFilterValue =
																				filterValue &&
																				Object.keys(filterValue)?.filter((f) => f !== filter.name);
																			const newFilteredValues = filteredFilterValue?.reduce(
																				(acc, curr) => {
																					return { ...acc, [curr]: filterValue?.[curr] };
																				},
																				{}
																			);

																			setFilterValue(newFilteredValues);
																			counter.current += 1;

																			// remove from filterType
																			const filteredFilterType =
																				filterType &&
																				Object.keys(filterType)?.filter((f) => f !== filter.name);
																			const newFilteredType = filteredFilterType?.reduce(
																				(acc, curr) => {
																					return { ...acc, [curr]: filterType?.[curr] };
																				},
																				{}
																			);

																			setFilterType(newFilteredType);

																			return;
																		}

																		setShow([...show, index]);
																	}}
																>
																	<div className="text-[18px] text-text flex items-center">
																		{noTranslations || filter.filterParam.includes("column") ? (
																			<>{filter.name}</>
																		) : (
																			<>{getFilterLabel(filter)}</>
																		)}
																		{filter.information && (
																			<div className="ml-1">
																				<Information information={filter.information} />
																			</div>
																		)}
																	</div>
																</Checkbox>
															) : (
																<div
																	className={classNames(
																		"flex flex-col",
																		!!filterType[filter.name] &&
																			!filterType[filter.name].includes("noFilter")
																			? "font-bold text-blue-action"
																			: ""
																	)}
																>
																	{!filter.filterParam.includes("columns")
																		? getFilterLabel(filter)
																		: filter.name}
																	{filter.information && (
																		<div className="ml-1">
																			<Information information={filter.information} />
																		</div>
																	)}
																</div>
															)}
														</div>
														{((hasCheckbox && show.includes(index)) ||
															typeof hasCheckbox !== "boolean") && (
															<>
																<div className="flex-col  w-[200px] mr-8">
																	<FilterDropdown
																		className={classNames(
																			"w-full mb-3",
																			filterType[filter.name] &&
																				!filterType[filter.name].includes("noFilter")
																				? "border border-action rounded-xl"
																				: ""
																		)}
																		data={[
																			{
																				id: "noFilter",
																				title: t(`filter.noFilter`),
																			},
																			...filter.filterByType.map((type) => {
																				return {
																					id: type,
																					title: t(`filter.${type}`),
																				};
																			}),
																		]}
																		defaultValue={{
																			id: "noFilter",
																			title: t(`filter.noFilter`),
																		}}
																		floating={true}
																		handleSelect={(value: AutoCompleteItem) => {
																			setFilterType({
																				...filterType,
																				[filter.name]: value.id as IFilterType,
																			});

																			const filteredFilterValue =
																				filterValue &&
																				Object.keys(filterValue)?.filter((f) => f !== filter.name);
																			const newFilteredValues = filteredFilterValue?.reduce(
																				(acc, curr) => {
																					if (value.id === "noFilter" && curr === filter.name) {
																						return { ...acc, [curr]: undefined };
																					}

																					return { ...acc, [curr]: filterValue?.[curr] };
																				},
																				{}
																			);

																			if (IBooleanFilterType.includes(value.id as IFilterType)) {
																				setFilterValue({
																					...newFilteredValues,
																					[filter.name]: {
																						filterParam: filter.filterParam,
																						subSelectFilterParam: filter.subSelectFilterParam,
																						type: value.id as IFilterType,
																					},
																				});

																				counter.current += 1;

																				return;
																			}

																			setFilterValue(newFilteredValues);
																			counter.current += 1;
																		}}
																		value={
																			filterType?.[filter.name]
																				? {
																						id: filterType[filter.name] as string,
																						title: t(`filter.${filterType?.[filter.name]}`),
																					}
																				: {
																						id: "noFilter",
																						title: t(`filter.noFilter`),
																					}
																		}
																	/>
																</div>
																<div className="flex flex-col max-w-[370px] w-1/3 ">
																	{filterType &&
																		Object.keys(filterType).map((fT, index) => {
																			if (fT !== filter.name) {
																				return <Fragment key={`ft-empty-${index}`} />;
																			}

																			if (ITextFilterType.some((type) => type === filterType[fT])) {
																				return (
																					<div key={`ft-${index}`}>
																						<InputField
																							className="mb-0"
																							handleChange={function (
																								event: ChangeEvent<HTMLInputElement>
																							): void {
																								setFilterValue({
																									...filterValue,
																									[filter.name]: {
																										value: event.target.value,
																										type: filterType[fT],
																										filterParam: filter.filterParam,
																										customColumnId: filter.customColumnId,
																									},
																								});
																								counter.current += 1;
																							}}
																							name={filter.name}
																							placeholder={
																								filter.filterParam.includes("column")
																									? filter.name
																									: getFilterLabel(filter)
																							}
																							value={
																								(filterValue?.[filter.name]?.value as string) || ""
																							}
																						/>
																					</div>
																				);
																			}

																			if (
																				filterType[fT] !== "between" &&
																				INumericFilterType.some((type) => type === filterType[fT])
																			) {
																				return (
																					<div key={`ft-${index}`}>
																						<InputField
																							error={filterValue?.[filter.name]?.value === ""}
																							handleChange={function (
																								event: ChangeEvent<HTMLInputElement>
																							): void {
																								setFilterValue({
																									...filterValue,
																									[filter.name]: {
																										value: event.target.value,
																										type: filterType[fT],
																										filterParam: filter.filterParam,
																										customColumnId: filter.customColumnId,
																									},
																								});
																								counter.current += 1;
																							}}
																							name={filter.name}
																							placeholder={getFilterLabel(filter)}
																							required={true}
																							showError={filterValue?.[filter.name]?.value === ""}
																							type="number"
																							value={
																								(filterValue?.[filter.name]?.value as number) || ""
																							}
																						/>
																					</div>
																				);
																			}

																			if (
																				filterType[fT] !== "betweenDate" &&
																				IDateFilterType.some((type) => type === filterType[fT])
																			) {
																				return (
																					<div key={`ft-${index}`}>
																						<InputField
																							error={filterValue?.[filter.name]?.hasError}
																							handleBlur={(
																								e: React.FocusEvent<HTMLInputElement>
																							) => {
																								if (e.target.value === "") {
																									setFilterValue({
																										...filterValue,
																										[filter.name]: {
																											...filterValue?.[filter.name],
																											hasError: true,
																										},
																									});
																								}
																							}}
																							handleChange={function (
																								event: ChangeEvent<HTMLInputElement>
																							): void {
																								setFilterValue({
																									...filterValue,
																									[filter.name]: {
																										value: event.target.value,
																										type: filterType[fT],
																										filterParam: filter.filterParam,
																										customColumnId: filter.customColumnId,
																									},
																								});
																								counter.current += 1;
																							}}
																							name={filter.name}
																							placeholder={getFilterLabel(filter)}
																							required={true}
																							showError={filterValue?.[filter.name]?.hasError}
																							type="date"
																							value={
																								(filterValue?.[filter.name]?.value as number) || ""
																							}
																						/>
																						{filterValue?.[filter.name]?.dateMode === "relative" &&
																							filterValue?.[filter.name]?.relativeDays !==
																								undefined && (
																								<span className="text-xs text-text-meta mt-1">
																									↻{" "}
																									{formatRelativeDays(
																										filterValue[filter.name].relativeDays!,
																										t
																									)}
																								</span>
																							)}
																					</div>
																				);
																			}

																			if (
																				filterType[fT] === "betweenDate" &&
																				IDateFilterType.some((type) => type === filterType[fT])
																			) {
																				return (
																					<div key={`ft-${index}`}>
																						<InputField
																							error={filterValue?.[filter.name]?.hasError}
																							handleBlur={(
																								e: React.FocusEvent<HTMLInputElement>
																							) => {
																								if (e.target.value === "") {
																									setFilterValue({
																										...filterValue,
																										[filter.name]: {
																											...filterValue?.[filter.name],
																											hasError: true,
																										},
																									});
																								}
																							}}
																							handleChange={function (
																								event: ChangeEvent<HTMLInputElement>
																							): void {
																								setFilterValue({
																									...filterValue,
																									[filter.name]: {
																										value: {
																											from: event.target.value,
																											till: (
																												filterValue?.[filter.name]?.value as {
																													from?: string;
																													till?: string;
																												}
																											)?.till as string,
																										},
																										type: "betweenDate",
																										filterParam: filter.filterParam,
																									},
																								});
																								counter.current += 1;
																							}}
																							name={`from_${filter.name}`}
																							placeholder={t(`from_${filter.name}`)}
																							required={true}
																							showError={filterValue?.[filter.name]?.hasError}
																							type="date"
																							value={
																								(
																									filterValue?.[filter.name]?.value as {
																										from?: string;
																										till?: string;
																									}
																								)?.from || ""
																							}
																						/>
																						{filterValue?.[filter.name]?.dateMode === "relative" &&
																							filterValue?.[filter.name]?.relativeDaysFrom !==
																								undefined && (
																								<span className="text-xs text-text-meta mt-1">
																									↻{" "}
																									{formatRelativeDays(
																										filterValue[filter.name].relativeDaysFrom!,
																										t
																									)}
																								</span>
																							)}
																						<InputField
																							handleChange={function (
																								event: ChangeEvent<HTMLInputElement>
																							): void {
																								setFilterValue({
																									...filterValue,
																									[filter.name]: {
																										value: {
																											till: event.target.value,
																											from: (
																												filterValue?.[filter.name]?.value as {
																													from?: string;
																													till?: string;
																												}
																											)?.from,
																										},
																										type: "betweenDate",
																										filterParam: filter.filterParam,
																									},
																								});
																								counter.current += 1;
																							}}
																							name={`till_${filter.name}`}
																							placeholder={t(`till_${filter.name}`)}
																							type="date"
																							value={
																								(
																									filterValue?.[filter.name]?.value as {
																										from?: string;
																										till?: string;
																									}
																								)?.till || ""
																							}
																						/>
																						{filterValue?.[filter.name]?.dateMode === "relative" &&
																							filterValue?.[filter.name]?.relativeDaysTill !==
																								undefined && (
																								<span className="text-xs text-text-meta mt-1">
																									↻{" "}
																									{formatRelativeDays(
																										filterValue[filter.name].relativeDaysTill!,
																										t
																									)}
																								</span>
																							)}
																					</div>
																				);
																			}

																			if (
																				filterType[fT] === "between" &&
																				INumericFilterType.some((type) => type === filterType[fT])
																			) {
																				return (
																					<div key={`ft-${index}`}>
																						<InputField
																							handleChange={function (
																								event: ChangeEvent<HTMLInputElement>
																							): void {
																								setFilterValue({
																									...filterValue,
																									[filter.name]: {
																										value: {
																											from: +event.target.value,
																											till: (
																												filterValue?.[filter.name]?.value as {
																													from?: number;
																													till?: number;
																												}
																											)?.till as number,
																										},
																										type: "between",
																										filterParam: filter.filterParam,
																									},
																								});
																								counter.current += 1;
																							}}
																							name={`from_${filter.name}`}
																							placeholder={t(`from_${filter.name}`)}
																							type="number"
																							value={
																								((
																									filterValue?.[filter.name]?.value as {
																										from?: number;
																										till?: number;
																									}
																								)?.from as number) || ""
																							}
																						/>
																						<InputField
																							handleChange={function (
																								event: ChangeEvent<HTMLInputElement>
																							): void {
																								setFilterValue({
																									...filterValue,
																									[filter.name]: {
																										value: {
																											till: +event.target.value,
																											from: (
																												filterValue?.[filter.name]?.value as {
																													from?: number;
																													till?: number;
																												}
																											)?.from as number,
																										},
																										type: "between",
																										filterParam: filter.filterParam,
																									},
																								});
																								counter.current += 1;
																							}}
																							name={`till_${filter.name}`}
																							placeholder={t(`till_${filter.name}`)}
																							type="number"
																							value={
																								((
																									filterValue?.[filter.name]?.value as {
																										from?: number;
																										till?: number;
																									}
																								)?.till as number) || ""
																							}
																						/>
																					</div>
																				);
																			}

																			if (
																				filterType[fT] === "dropdown" &&
																				fT === "inclusionStatus"
																			) {
																				return (
																					<div key={`ft-${index}`}>
																						<FilterDropdown
																							className="w-full mb-3"
																							data={[
																								{
																									title: InclusionFilterStatus.ADDED,
																									id: InclusionFilterStatus.ADDED,
																								},
																								{
																									title: InclusionFilterStatus.UNSURE,
																									id: InclusionFilterStatus.UNSURE,
																								},
																								{
																									title: InclusionFilterStatus.NOT_FOUND,
																									id: InclusionFilterStatus.NOT_FOUND,
																								},
																								{
																									title: InclusionFilterStatus.BOTH,
																									id: InclusionFilterStatus.BOTH,
																								},
																							]}
																							floating={true}
																							handleSelect={(value: AutoCompleteItem) => {
																								if (value.id) {
																									setFilterValue({
																										...filterValue,
																										[filter.name]: {
																											value: value.id,
																											type: filterType[fT],
																											filterParam: filter.filterParam,
																										},
																									});
																									counter.current += 1;
																								}
																							}}
																						/>
																					</div>
																				);
																			} else if (
																				filterType[fT] === "dropdown" ||
																				filterType[fT] === "dropdownExclude"
																			) {
																				return (
																					<Autocomplete
																						key={`ft-${index}`}
																						multiple
																						data-testid={`autocomplete-${filter.name}`}
																						defaultValue={[]}
																						getOptionLabel={(option) => option.title}
																						inputValue={searchValue?.[filter.name] || ""}
																						isOptionEqualToValue={(option, currentValue) => {
																							if (
																								currentValue.title === "" &&
																								currentValue.id === ""
																							) {
																								return true;
																							}

																							return option.id === currentValue.id;
																						}}
																						loading={isLoadingByPropertyName === filter.name}
																						loadingText="Searching..."
																						noOptionsText={
																							searchValue?.[filter.name] &&
																							searchValue?.[filter.name]?.length > 0
																								? "Typing..."
																								: "Start typing to search"
																						}
																						options={currentFilter?.[filter.name] || []}
																						popupIcon={
																							<Icon
																								className="text-gray-500 h-[24px] w-[15px]"
																								icon={faChevronDown}
																							/>
																						}
																						renderInput={(params) => (
																							<TextField
																								sx={{
																									width: "100%",
																									minHeight: 48,
																									marginBottom: 1,
																									"& .MuiFilledInput-root": {
																										backgroundColor: "white",
																										minHeight: 48,
																										marginBottom: 0,
																										borderRadius: 1.5,
																										paddingTop: "10px",
																										paddingBottom: "10px !important",
																										paddingLeft: 1.5,
																										"& .MuiChip-root": {
																											backgroundColor: "#EAEEFF",
																											color: "#1D3DF2",
																										},
																									},
																								}}
																								{...params}
																							/>
																						)}
																						size="small"
																						sx={{
																							width: "100%",
																						}}
																						value={
																							(filterValue?.[filter.name]?.value as {
																								title: string;
																								id: string;
																							}[]) || []
																						}
																						onBlur={(e) => {
																							e.preventDefault();
																							e.stopPropagation();
																						}}
																						onChange={(_event, value) => {
																							setFilterValue({
																								...filterValue,
																								[filter.name]: {
																									value: value,
																									type: filterType[fT],
																									filterParam: filter.filterParam,
																								},
																							});
																							counter.current += 1;

																							setSearchValue({});
																						}}
																						onInputChange={(_event, newValue, reason) => {
																							if (reason === "input") {
																								setSearchValue({ [filter.name]: newValue });
																							}
																						}}
																					/>
																				);
																			} else if (
																				filterType[fT] === "dropdownCustom" ||
																				filterType[fT] === "dropdownCustomExclude"
																			) {
																				return (
																					<Autocomplete
																						key={`ft-${index}`}
																						multiple
																						clearOnBlur={true}
																						data-testid={`autocomplete-${filter.name}`}
																						getOptionLabel={(option) => option.title}
																						inputValue={inputValues || ""}
																						isOptionEqualToValue={(option, currentValue) => {
																							if (
																								currentValue.title === "" &&
																								currentValue.id === ""
																							) {
																								return true;
																							}

																							return option.id === currentValue.id;
																						}}
																						loading={isLoadingByPropertyName === filter.name}
																						loadingText="Searching..."
																						noOptionsText={
																							searchValue?.[filter.name] &&
																							searchValue?.[filter.name]?.length > 0
																								? "Typing..."
																								: "Start typing to search"
																						}
																						options={
																							[
																								{
																									id: inputValues || "",
																									title: inputValues || "",
																								},
																								...(currentFilter?.[filter.name] || []),
																							]?.filter((r) => r.id !== "") || []
																						}
																						popupIcon={
																							<Icon
																								className="text-gray-500 h-[24px] w-[15px]"
																								icon={faChevronDown}
																							/>
																						}
																						renderInput={(params) => (
																							<TextField
																								sx={{
																									width: "100%",
																									minHeight: 48,
																									marginBottom: 1,
																									"& .MuiFilledInput-root": {
																										backgroundColor: "white",
																										minHeight: 48,
																										marginBottom: 0,
																										borderRadius: 1.5,
																										paddingTop: "10px",
																										paddingBottom: "10px !important",
																										paddingLeft: 1.5,
																										"& .MuiChip-root": {
																											backgroundColor: "#EAEEFF",
																											color: "#1D3DF2",
																										},
																									},
																								}}
																								{...params}
																							/>
																						)}
																						size="small"
																						sx={{
																							width: "100%",
																						}}
																						value={
																							(filterValue?.[filter.name]?.value as {
																								title: string;
																								id: string;
																							}[]) || []
																						}
																						onBlur={(e) => {
																							e.preventDefault();
																							e.stopPropagation();
																							setSearchValue({});
																						}}
																						onChange={(_event, value) => {
																							setFilterValue({
																								...filterValue,
																								[filter.name]: {
																									value: value,
																									type: filterType[fT],
																									filterParam: filter.filterParam,
																								},
																							});
																							counter.current += 1;
																							setInputValues("");
																							setSearchValue({});
																						}}
																						onInputChange={(_event, newValue, reason) => {
																							if (reason === "input") {
																								setInputValues(newValue);
																								setSearchValue({ [filter.name]: newValue });
																							}
																						}}
																					/>
																				);
																			}

																			return <Fragment key={`ft-fallback-${index}`} />;
																		})}
																</div>
															</>
														)}
													</div>
												);
											})}
										</div>
									)}
								</div>
							);
						})
					: FilterDataType.map((filter, index) => {
							return (
								<div key={`${filter.name}-${index}`} className="flex flex-row items-start w-full">
									<div className="flex flex-row w-[200px] mr-8 items-center h-[48px]">
										{hasCheckbox ? (
											<Checkbox
												isChecked={show.includes(index)}
												onChange={() => {
													setPrefill(false);
													if (show.includes(index)) {
														setShow(show.filter((s) => s !== index));

														// remove from filterValue
														const filteredFilterValue =
															filterValue &&
															Object.keys(filterValue)?.filter((f) => f !== filter.name);
														const newFilteredValues = filteredFilterValue?.reduce((acc, curr) => {
															return { ...acc, [curr]: filterValue?.[curr] };
														}, {});

														setFilterValue(newFilteredValues);
														counter.current += 1;

														// remove from filterType
														const filteredFilterType =
															filterType &&
															Object.keys(filterType)?.filter((f) => f !== filter.name);
														const newFilteredType = filteredFilterType?.reduce((acc, curr) => {
															return { ...acc, [curr]: filterType?.[curr] };
														}, {});

														setFilterType(newFilteredType);

														return;
													}

													setShow([...show, index]);
												}}
											>
												<div className="text-[18px] text-text flex items-center">
													{noTranslations || filter.filterParam.includes("column") ? (
														<>{filter.name}</>
													) : (
														<>{getFilterLabel(filter)}</>
													)}
													{filter.information && (
														<div className="ml-1">
															<Information information={filter.information} />
														</div>
													)}
												</div>
											</Checkbox>
										) : (
											<div
												className={classNames(
													"flex flex-col",
													!!filterType[filter.name] && !filterType[filter.name].includes("noFilter")
														? "font-bold text-blue-action"
														: ""
												)}
											>
												{!filter.filterParam.includes("columns")
													? getFilterLabel(filter)
													: filter.name}
												{filter.information && (
													<div className="ml-1">
														<Information information={filter.information} />
													</div>
												)}
											</div>
										)}
									</div>
									{((hasCheckbox && show.includes(index)) || typeof hasCheckbox !== "boolean") && (
										<>
											<div className="flex-col  w-[200px] mr-8">
												<FilterDropdown
													className={classNames(
														"w-full mb-3",
														filterType[filter.name] && !filterType[filter.name].includes("noFilter")
															? "border border-action rounded-xl"
															: ""
													)}
													data={[
														{
															id: "noFilter",
															title: t(`filter.noFilter`),
														},
														...filter.filterByType.map((type) => {
															return {
																id: type,
																title: t(`filter.${type}`),
															};
														}),
													]}
													defaultValue={{
														id: "noFilter",
														title: t(`filter.noFilter`),
													}}
													floating={true}
													handleSelect={(value: AutoCompleteItem) => {
														setFilterType({
															...filterType,
															[filter.name]: value.id as IFilterType,
														});

														const filteredFilterValue =
															filterValue &&
															Object.keys(filterValue)?.filter((f) => f !== filter.name);
														const newFilteredValues = filteredFilterValue?.reduce((acc, curr) => {
															if (value.id === "noFilter" && curr === filter.name) {
																return { ...acc, [curr]: undefined };
															}

															return { ...acc, [curr]: filterValue?.[curr] };
														}, {});

														if (IBooleanFilterType.includes(value.id as IFilterType)) {
															setFilterValue({
																...newFilteredValues,
																[filter.name]: {
																	filterParam: filter.filterParam,
																	subSelectFilterParam: filter.subSelectFilterParam,
																	type: value.id as IFilterType,
																},
															});

															counter.current += 1;

															return;
														}

														setFilterValue(newFilteredValues);
														counter.current += 1;
													}}
													value={
														filterType?.[filter.name]
															? {
																	id: filterType[filter.name] as string,
																	title: t(`filter.${filterType?.[filter.name]}`),
																}
															: {
																	id: "noFilter",
																	title: t(`filter.noFilter`),
																}
													}
												/>
											</div>
											<div className="flex flex-col max-w-[370px] w-1/3 ">
												{filterType &&
													Object.keys(filterType).map((fT, index) => {
														if (fT !== filter.name) {
															return <Fragment key={`ft-empty-${index}`} />;
														}

														if (ITextFilterType.some((type) => type === filterType[fT])) {
															return (
																<div key={`ft-${index}`}>
																	<InputField
																		className="mb-0"
																		handleChange={function (
																			event: ChangeEvent<HTMLInputElement>
																		): void {
																			setFilterValue({
																				...filterValue,
																				[filter.name]: {
																					value: event.target.value,
																					type: filterType[fT],
																					filterParam: filter.filterParam,
																					customColumnId: filter.customColumnId,
																				},
																			});
																			counter.current += 1;
																		}}
																		name={filter.name}
																		placeholder={
																			filter.filterParam.includes("column")
																				? filter.name
																				: getFilterLabel(filter)
																		}
																		value={(filterValue?.[filter.name]?.value as string) || ""}
																	/>
																</div>
															);
														}

														if (
															filterType[fT] !== "between" &&
															INumericFilterType.some((type) => type === filterType[fT])
														) {
															return (
																<div key={`ft-${index}`}>
																	<InputField
																		error={filterValue?.[filter.name]?.value === ""}
																		handleChange={function (
																			event: ChangeEvent<HTMLInputElement>
																		): void {
																			setFilterValue({
																				...filterValue,
																				[filter.name]: {
																					value: event.target.value,
																					type: filterType[fT],
																					filterParam: filter.filterParam,
																					customColumnId: filter.customColumnId,
																				},
																			});
																			counter.current += 1;
																		}}
																		name={filter.name}
																		placeholder={getFilterLabel(filter)}
																		required={true}
																		showError={filterValue?.[filter.name]?.value === ""}
																		type="number"
																		value={(filterValue?.[filter.name]?.value as number) || ""}
																	/>
																</div>
															);
														}

														if (
															filterType[fT] !== "betweenDate" &&
															IDateFilterType.some((type) => type === filterType[fT])
														) {
															return (
																<div key={`ft-${index}`}>
																	<InputField
																		error={filterValue?.[filter.name]?.hasError}
																		handleBlur={(e: React.FocusEvent<HTMLInputElement>) => {
																			if (e.target.value === "") {
																				setFilterValue({
																					...filterValue,
																					[filter.name]: {
																						...filterValue?.[filter.name],
																						hasError: true,
																					},
																				});
																			}
																		}}
																		handleChange={function (
																			event: ChangeEvent<HTMLInputElement>
																		): void {
																			setFilterValue({
																				...filterValue,
																				[filter.name]: {
																					value: event.target.value,
																					type: filterType[fT],
																					filterParam: filter.filterParam,
																					customColumnId: filter.customColumnId,
																				},
																			});
																			counter.current += 1;
																		}}
																		name={filter.name}
																		placeholder={getFilterLabel(filter)}
																		required={true}
																		showError={filterValue?.[filter.name]?.hasError}
																		type="date"
																		value={(filterValue?.[filter.name]?.value as number) || ""}
																	/>
																	{filterValue?.[filter.name]?.dateMode === "relative" &&
																		filterValue?.[filter.name]?.relativeDays !== undefined && (
																			<span className="text-xs text-text-meta mt-1">
																				↻{" "}
																				{formatRelativeDays(
																					filterValue[filter.name].relativeDays!,
																					t
																				)}
																			</span>
																		)}
																</div>
															);
														}

														if (
															filterType[fT] === "betweenDate" &&
															IDateFilterType.some((type) => type === filterType[fT])
														) {
															return (
																<div key={`ft-${index}`}>
																	<InputField
																		error={filterValue?.[filter.name]?.hasError}
																		handleBlur={(e: React.FocusEvent<HTMLInputElement>) => {
																			if (e.target.value === "") {
																				setFilterValue({
																					...filterValue,
																					[filter.name]: {
																						...filterValue?.[filter.name],
																						hasError: true,
																					},
																				});
																			}
																		}}
																		handleChange={function (
																			event: ChangeEvent<HTMLInputElement>
																		): void {
																			setFilterValue({
																				...filterValue,
																				[filter.name]: {
																					value: {
																						from: event.target.value,
																						till: (
																							filterValue?.[filter.name]?.value as {
																								from?: string;
																								till?: string;
																							}
																						)?.till as string,
																					},
																					type: "betweenDate",
																					filterParam: filter.filterParam,
																				},
																			});
																			counter.current += 1;
																		}}
																		name={`from_${filter.name}`}
																		placeholder={t(`from_${filter.name}`)}
																		required={true}
																		showError={filterValue?.[filter.name]?.hasError}
																		type="date"
																		value={
																			(
																				filterValue?.[filter.name]?.value as {
																					from?: string;
																					till?: string;
																				}
																			)?.from || ""
																		}
																	/>
																	{filterValue?.[filter.name]?.dateMode === "relative" &&
																		filterValue?.[filter.name]?.relativeDaysFrom !== undefined && (
																			<span className="text-xs text-text-meta mt-1">
																				↻{" "}
																				{formatRelativeDays(
																					filterValue[filter.name].relativeDaysFrom!,
																					t
																				)}
																			</span>
																		)}
																	<InputField
																		handleChange={function (
																			event: ChangeEvent<HTMLInputElement>
																		): void {
																			setFilterValue({
																				...filterValue,
																				[filter.name]: {
																					value: {
																						till: event.target.value,
																						from: (
																							filterValue?.[filter.name]?.value as {
																								from?: string;
																								till?: string;
																							}
																						)?.from,
																					},
																					type: "betweenDate",
																					filterParam: filter.filterParam,
																				},
																			});
																			counter.current += 1;
																		}}
																		name={`till_${filter.name}`}
																		placeholder={t(`till_${filter.name}`)}
																		type="date"
																		value={
																			(
																				filterValue?.[filter.name]?.value as {
																					from?: string;
																					till?: string;
																				}
																			)?.till || ""
																		}
																	/>
																	{filterValue?.[filter.name]?.dateMode === "relative" &&
																		filterValue?.[filter.name]?.relativeDaysTill !== undefined && (
																			<span className="text-xs text-text-meta mt-1">
																				↻{" "}
																				{formatRelativeDays(
																					filterValue[filter.name].relativeDaysTill!,
																					t
																				)}
																			</span>
																		)}
																</div>
															);
														}

														if (
															filterType[fT] === "between" &&
															INumericFilterType.some((type) => type === filterType[fT])
														) {
															return (
																<div key={`ft-${index}`}>
																	<InputField
																		handleChange={function (
																			event: ChangeEvent<HTMLInputElement>
																		): void {
																			setFilterValue({
																				...filterValue,
																				[filter.name]: {
																					value: {
																						from: +event.target.value,
																						till: (
																							filterValue?.[filter.name]?.value as {
																								from?: number;
																								till?: number;
																							}
																						)?.till as number,
																					},
																					type: "between",
																					filterParam: filter.filterParam,
																				},
																			});
																			counter.current += 1;
																		}}
																		name={`from_${filter.name}`}
																		placeholder={t(`from_${filter.name}`)}
																		type="number"
																		value={
																			((
																				filterValue?.[filter.name]?.value as {
																					from?: number;
																					till?: number;
																				}
																			)?.from as number) || ""
																		}
																	/>
																	<InputField
																		handleChange={function (
																			event: ChangeEvent<HTMLInputElement>
																		): void {
																			setFilterValue({
																				...filterValue,
																				[filter.name]: {
																					value: {
																						till: +event.target.value,
																						from: (
																							filterValue?.[filter.name]?.value as {
																								from?: number;
																								till?: number;
																							}
																						)?.from as number,
																					},
																					type: "between",
																					filterParam: filter.filterParam,
																				},
																			});
																			counter.current += 1;
																		}}
																		name={`till_${filter.name}`}
																		placeholder={t(`till_${filter.name}`)}
																		type="number"
																		value={
																			((
																				filterValue?.[filter.name]?.value as {
																					from?: number;
																					till?: number;
																				}
																			)?.till as number) || ""
																		}
																	/>
																</div>
															);
														}

														if (filterType[fT] === "dropdown" && fT === "inclusionStatus") {
															return (
																<div key={`ft-${index}`}>
																	<FilterDropdown
																		className="w-full mb-3"
																		data={[
																			{
																				title: InclusionFilterStatus.ADDED,
																				id: InclusionFilterStatus.ADDED,
																			},
																			{
																				title: InclusionFilterStatus.UNSURE,
																				id: InclusionFilterStatus.UNSURE,
																			},
																			{
																				title: InclusionFilterStatus.NOT_FOUND,
																				id: InclusionFilterStatus.NOT_FOUND,
																			},
																			{
																				title: InclusionFilterStatus.BOTH,
																				id: InclusionFilterStatus.BOTH,
																			},
																		]}
																		floating={true}
																		handleSelect={(value: AutoCompleteItem) => {
																			if (value.id) {
																				setFilterValue({
																					...filterValue,
																					[filter.name]: {
																						value: value.id,
																						type: filterType[fT],
																						filterParam: filter.filterParam,
																					},
																				});
																				counter.current += 1;
																			}
																		}}
																	/>
																</div>
															);
														} else if (
															filterType[fT] === "dropdown" ||
															filterType[fT] === "dropdownExclude"
														) {
															return (
																<Autocomplete
																	key={`ft-${index}`}
																	multiple
																	data-testid={`autocomplete-${filter.name}`}
																	defaultValue={[]}
																	getOptionLabel={(option) => option.title}
																	inputValue={searchValue?.[filter.name] || ""}
																	isOptionEqualToValue={(option, currentValue) => {
																		if (currentValue.title === "" && currentValue.id === "") {
																			return true;
																		}

																		return option.id === currentValue.id;
																	}}
																	loading={isLoadingByPropertyName === filter.name}
																	loadingText="Searching..."
																	noOptionsText={
																		searchValue?.[filter.name] &&
																		searchValue?.[filter.name]?.length > 0
																			? "Typing..."
																			: "Start typing to search"
																	}
																	options={currentFilter?.[filter.name] || []}
																	popupIcon={
																		<Icon
																			className="text-gray-500 h-[24px] w-[15px]"
																			icon={faChevronDown}
																		/>
																	}
																	renderInput={(params) => (
																		<TextField
																			sx={{
																				width: "100%",
																				minHeight: 48,
																				marginBottom: 1,
																				"& .MuiFilledInput-root": {
																					backgroundColor: "white",
																					minHeight: 48,
																					marginBottom: 0,
																					borderRadius: 1.5,
																					paddingTop: "10px",
																					paddingBottom: "10px !important",
																					paddingLeft: 1.5,
																					"& .MuiChip-root": {
																						backgroundColor: "#EAEEFF",
																						color: "#1D3DF2",
																					},
																				},
																			}}
																			{...params}
																		/>
																	)}
																	size="small"
																	sx={{
																		width: "100%",
																	}}
																	value={
																		(filterValue?.[filter.name]?.value as {
																			title: string;
																			id: string;
																		}[]) || []
																	}
																	onBlur={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																	}}
																	onChange={(_event, value) => {
																		setFilterValue({
																			...filterValue,
																			[filter.name]: {
																				value: value,
																				type: filterType[fT],
																				filterParam: filter.filterParam,
																			},
																		});
																		counter.current += 1;

																		setSearchValue({});
																	}}
																	onInputChange={(_event, newValue, reason) => {
																		if (reason === "input") {
																			setSearchValue({ [filter.name]: newValue });
																		}
																	}}
																/>
															);
														} else if (
															filterType[fT] === "dropdownCustom" ||
															filterType[fT] === "dropdownCustomExclude"
														) {
															return (
																<Autocomplete
																	key={`ft-${index}`}
																	multiple
																	clearOnBlur={true}
																	data-testid={`autocomplete-${filter.name}`}
																	getOptionLabel={(option) => option.title}
																	inputValue={inputValues || ""}
																	isOptionEqualToValue={(option, currentValue) => {
																		if (currentValue.title === "" && currentValue.id === "") {
																			return true;
																		}

																		return option.id === currentValue.id;
																	}}
																	loading={isLoadingByPropertyName === filter.name}
																	loadingText="Searching..."
																	noOptionsText={
																		searchValue?.[filter.name] &&
																		searchValue?.[filter.name]?.length > 0
																			? "Typing..."
																			: "Start typing to search"
																	}
																	options={
																		[
																			{ id: inputValues || "", title: inputValues || "" },
																			...(currentFilter?.[filter.name] || []),
																		]?.filter((r) => r.id !== "") || []
																	}
																	popupIcon={
																		<Icon
																			className="text-gray-500 h-[24px] w-[15px]"
																			icon={faChevronDown}
																		/>
																	}
																	renderInput={(params) => (
																		<TextField
																			sx={{
																				width: "100%",
																				minHeight: 48,
																				marginBottom: 1,
																				"& .MuiFilledInput-root": {
																					backgroundColor: "white",
																					minHeight: 48,
																					marginBottom: 0,
																					borderRadius: 1.5,
																					paddingTop: "10px",
																					paddingBottom: "10px !important",
																					paddingLeft: 1.5,
																					"& .MuiChip-root": {
																						backgroundColor: "#EAEEFF",
																						color: "#1D3DF2",
																					},
																				},
																			}}
																			{...params}
																		/>
																	)}
																	size="small"
																	sx={{
																		width: "100%",
																	}}
																	value={
																		(filterValue?.[filter.name]?.value as {
																			title: string;
																			id: string;
																		}[]) || []
																	}
																	onBlur={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																		setSearchValue({});
																	}}
																	onChange={(_event, value) => {
																		setFilterValue({
																			...filterValue,
																			[filter.name]: {
																				value: value,
																				type: filterType[fT],
																				filterParam: filter.filterParam,
																			},
																		});
																		counter.current += 1;
																		setInputValues("");
																		setSearchValue({});
																	}}
																	onInputChange={(_event, newValue, reason) => {
																		if (reason === "input") {
																			setInputValues(newValue);
																			setSearchValue({ [filter.name]: newValue });
																		}
																	}}
																/>
															);
														}

														return <Fragment key={`ft-fallback-${index}`} />;
													})}
											</div>
										</>
									)}
								</div>
							);
						})}
			</div>
		);
	};

	return {
		renderFilters,
		filterType,
		filterValue,
		setFilterType,
		setFilterValue,
		isDirty: counter.current > 0,
	};
};
